import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  catchError,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiEnvelope } from 'src/app/modules/events/services/event.service';

/** KPI retornado pela API (valor + delta vs mês anterior). */
export interface DashboardKpi {
  value: number;
  change_percent: number;
  comparison: string;
}

export interface DashboardKpis {
  total_sales_brl: DashboardKpi;
  total_companies: DashboardKpi;
  affiliate_members: DashboardKpi;
  events_held: DashboardKpi;
}

export interface DashboardMonthPoint {
  year_month: string;
  label: string;
  members_new: number;
  event_registrations: number;
  sales_brl: number;
}

export interface DashboardCharts {
  months: DashboardMonthPoint[];
}

export interface DashboardAnalyticsContent {
  kpis: DashboardKpis;
  charts: DashboardCharts;
}

@Injectable({ providedIn: 'root' })
export class DashboardAnalyticsService {
  private readonly baseUrl = environment.apiUrl;
  /** Contrato: GET /private/cms/dashboard/get_analytics (sem query/body). */
  private readonly analyticsUrl = `${this.baseUrl}/private/cms/dashboard/get_analytics`;

  /** Dispara novo GET (ex.: após erro ou botão "Tentar novamente"). */
  private readonly reload$ = new Subject<void>();

  /**
   * Primeira emissão ao assinar; novas emissões quando `invalidateCache()` é chamado.
   * `shareReplay` evita requisições duplicadas entre header e página do dashboard.
   */
  private readonly analytics$: Observable<DashboardAnalyticsContent> = merge(
    of(undefined),
    this.reload$
  ).pipe(
    switchMap(() =>
      this.http
        .get<ApiEnvelope<DashboardAnalyticsContent>>(this.analyticsUrl, this.privateOptions())
        .pipe(
          map((res) => {
            if (res.status !== 'SUCCESS' || !res.content) {
              throw new Error(res.message || 'Não foi possível carregar os analytics.');
            }
            return res.content;
          }),
          catchError((err) =>
            this.handleError(
              err,
              'Não foi possível carregar os indicadores. Tente novamente em instantes.'
            )
          )
        )
    ),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(private http: HttpClient) {}

  private getToken(): string | null {
    return (
      localStorage.getItem('cms_token') ||
      localStorage.getItem('app_token') ||
      sessionStorage.getItem('cms_token') ||
      sessionStorage.getItem('app_token')
    );
  }

  private privateOptions() {
    const token = this.getToken();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    return { headers };
  }

  getAnalytics(): Observable<DashboardAnalyticsContent> {
    return this.analytics$;
  }

  /** Força novo GET; assinantes existentes (header + dashboard) recebem o próximo valor. */
  invalidateCache(): void {
    this.reload$.next();
  }

  private handleError(err: unknown, fallbackMessage: string): Observable<never> {
    let message = fallbackMessage;

    if (err instanceof Error && err.message?.trim()) {
      message = err.message.trim();
    }

    if (err instanceof HttpErrorResponse) {
      const raw = err.error;
      if (typeof raw === 'string' && raw.trim()) {
        message = raw.trim();
      } else if (raw && typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        const msg = o['message'];
        if (typeof msg === 'string' && msg.trim()) {
          message = msg.trim();
        } else {
          const nested = o['error'];
          if (nested && typeof nested === 'object') {
            const em = (nested as Record<string, unknown>)['message'];
            if (typeof em === 'string' && em.trim()) {
              message = em.trim();
            }
          }
        }
      }
    }

    return throwError(() => new Error(message));
  }
}

export function formatDashboardBrl(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/** Valor absoluto do percentual para o card (o template acrescenta "%"). */
export function formatDashboardDeltaPercent(changePercent: number): string {
  return Math.abs(changePercent).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export function dashboardKpiArrow(changePercent: number): 'up' | 'down' {
  return changePercent >= 0 ? 'up' : 'down';
}

export function dashboardKpiDeltaColor(changePercent: number): string {
  if (changePercent > 0) return 'text-emerald-500';
  if (changePercent < 0) return 'text-red-500';
  return 'text-blueGray-400';
}

export function formatDashboardInteger(value: number): string {
  return Math.round(value).toLocaleString('pt-BR');
}
